import React from 'react';
import { Alert } from '@fx-ui/fine-design';
import { Hyperlink } from '@fx-ui/jdy-design-biz';
import { URL_PARAMETER, openInNewTab } from '@libs/shared';
import { platformIntl } from '../../../constants';
import { VerifyProfile, VerifyStatus } from '../../../interfaces';
import { authModel } from '../../../models';
import siteRouterManager, { SiteRouterKey } from '../../../utils/site-router';
import { SubmittedAuthType, type AuthTarget, type BlockKind, type GuardOptions } from '../types';

interface DerivedStatus {
    corp: VerifyStatus;
    owner: VerifyStatus;
    verified: boolean;
    blockKind: BlockKind;
    submittedAuthType?: SubmittedAuthType;
    nextAuthTarget: AuthTarget;
    canVerify: boolean;
}

export const runEntryClick = (
    onEntryClick: GuardOptions['onEntryClick'],
    target: AuthTarget,
    open: () => void
) => {
    try {
        onEntryClick?.(target);
    } catch {
        // 业务侧入口点击回调异常不应阻断认证主流程。
    }
    open();
};

const getVerifyRouter = (target: AuthTarget) => {
    switch (target) {
        case 'corp':
            return siteRouterManager.getQueryPath(
                [{ key: URL_PARAMETER.TENANT_VERIFY_TYPE, value: 'corp' }],
                SiteRouterKey.IdentityVerify,
                authModel.tenantId
            );
        case 'owner':
            return siteRouterManager.getQueryPath(
                [{ key: URL_PARAMETER.TENANT_VERIFY_TYPE, value: 'owner' }],
                SiteRouterKey.IdentityVerify,
                authModel.tenantId
            );
        case 'select':
        default:
            return siteRouterManager.getPath(SiteRouterKey.IdentityVerify, authModel.tenantId);
    }
};

const getAuthTypeText = (type?: SubmittedAuthType) => {
    switch (type) {
        case SubmittedAuthType.Corp:
            return platformIntl.get('fxp.base.bg.corp_info.corp_auth.opt');
        case SubmittedAuthType.Owner:
            return platformIntl.get('fxp.base.bg.corp_info.personal_auth.opt');
        case SubmittedAuthType.Identity:
        default:
            return platformIntl.get('fxp.base.bg.corp_info.identity_auth.nm');
    }
};

export const getDerivedStatus = (profile?: VerifyProfile): DerivedStatus => {
    const corp = profile?.corp.status || VerifyStatus.Unverified;
    const owner = profile?.owner.status || VerifyStatus.Unverified;
    const verified = corp === VerifyStatus.Approved || owner === VerifyStatus.Approved;
    const canVerify = authModel.hasSysAdmin;

    if (verified) {
        return {
            corp,
            owner,
            verified,
            blockKind: 'none',
            nextAuthTarget: 'select',
            canVerify
        };
    }

    const corpReviewing = corp === VerifyStatus.Open;
    const ownerReviewing = owner === VerifyStatus.Open;

    if (corpReviewing || ownerReviewing) {
        const submittedAuthType =
            corpReviewing && ownerReviewing
                ? SubmittedAuthType.Identity
                : corpReviewing
                ? SubmittedAuthType.Corp
                : SubmittedAuthType.Owner;
        const nextAuthTarget = corpReviewing && !ownerReviewing ? 'owner' : 'corp';

        return {
            corp,
            owner,
            verified,
            blockKind: 'reviewing',
            submittedAuthType,
            nextAuthTarget,
            canVerify
        };
    }

    return {
        corp,
        owner,
        verified,
        blockKind: 'unverified',
        nextAuthTarget: 'select',
        canVerify
    };
};

export const openVerifyPage = (target: AuthTarget = 'select') => {
    openInNewTab(getVerifyRouter(target), '_blank');
};

export const openVerificationAlert = (
    profile?: VerifyProfile,
    options: { helpUrl?: string } & GuardOptions = {}
) => {
    const status = getDerivedStatus(profile);

    if (status.verified) {
        return;
    }

    const { helpUrl, onEntryClick } = options;
    const isReviewing = status.blockKind === 'reviewing';
    const actionable =
        status.canVerify &&
        (!isReviewing || status.submittedAuthType !== SubmittedAuthType.Identity);
    const authType = getAuthTypeText(status.submittedAuthType);
    const defaultTitle = isReviewing
        ? platformIntl.get('fxp.base.bg.corp_info.unusable_wait_auth_review.tip')
        : platformIntl.get('fxp.base.bg.corp_info.identity_auth_to_use_function.tip');
    const defaultMessage = isReviewing
        ? platformIntl.get(
              status.canVerify
                  ? 'fxp.base.bg.corp_info.auth_submitted_wait_review.tip'
                  : 'fxp.base.bg.corp_info.auth_submitted_wait_review_admin.tip',
              { authtype: authType }
          )
        : platformIntl.get(
              status.canVerify
                  ? 'fxp.base.bg.corp_info.auth_for_security_compliance.tip'
                  : 'fxp.base.bg.corp_info.auth_for_security_admin.tip'
          );

    Alert.openAlert({
        type: 'warning',
        title: defaultTitle,
        message: defaultMessage,
        footerLeft: helpUrl ? (
            <Hyperlink underline compact href={helpUrl} target="jdy_doc">
                {platformIntl.get('fxp.learn_more.btn')}
            </Hyperlink>
        ) : undefined,
        text4Cancel: actionable
            ? isReviewing
                ? platformIntl.get('fxp.got_it.btn')
                : platformIntl.get('fxp.base.bg.corp_info.auth_later.btn')
            : null,
        text4Ok: actionable
            ? status.nextAuthTarget === 'owner'
                ? platformIntl.get('fxp.base.bg.corp_info.personal_auth.btn')
                : status.nextAuthTarget === 'corp'
                ? platformIntl.get('fxp.base.bg.corp_info.corp_auth.btn')
                : platformIntl.get('fxp.base.bg.corp_info.auth_now.btn')
            : platformIntl.get('fxp.got_it.btn'),
        onOk: () => {
            if (actionable) {
                runEntryClick(onEntryClick, status.nextAuthTarget, () => {
                    openVerifyPage(status.nextAuthTarget);
                    Alert.closeAlert();
                });
                return;
            }
            Alert.closeAlert();
        },
        onCancel: Alert.closeAlert
    });
};
